import React, { useMemo, useState } from 'react';
import AccountFormModal from '../components/AccountFormModal';
import ConfirmModal from '../components/ConfirmModal';
import PasswordCard from '../components/PasswordCard';
import useGridColumns from '../hooks/useGridColumns';
import usePasswords from '../hooks/usePasswords';
import { createPassword, deletePassword, updatePassword } from '../services/passwordsApi';

const PasswordsList = () => {
  const { passwords, loading, error, refetch } = usePasswords();
  const [search, setSearch] = useState('');
  const [formModal, setFormModal] = useState({ open: false, mode: 'add', password: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, password: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const gridColumns = useGridColumns();
  const gridStyle = {
    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
  };

  const filteredPasswords = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return passwords;

    return passwords.filter((password) => {
      const haystack = [
        password.account,
        password.username,
        password.url,
        password.note,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [passwords, search]);

  const openAddForm = () => {
    setFormError(null);
    setFormModal({ open: true, mode: 'add', password: null });
  };

  const openEditForm = (password) => {
    setFormError(null);
    setFormModal({ open: true, mode: 'edit', password });
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModal({ open: false, mode: 'add', password: null });
    setFormError(null);
  };

  const openDeleteModal = (password) => {
    setDeleteError(null);
    setDeleteModal({ open: true, password });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModal({ open: false, password: null });
    setDeleteError(null);
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      setFormError(null);

      if (formModal.mode === 'edit') {
        await updatePassword(formModal.password.id, formData);
      } else {
        await createPassword(formData);
      }

      await refetch();
      closeFormModal();
    } catch (err) {
      setFormError(err.message || 'Failed to save account');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.password?.id) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await deletePassword(deleteModal.password.id);
      await refetch();
      closeDeleteModal();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete account');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <header className="mb-4">
          {/* Mobile: search + add on same line */}
          <div className="flex sm:hidden gap-2 items-center w-full">
            <div className="relative flex-1 min-w-0">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search"
                className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-gray-400"
                aria-label="Search passwords"
              />
            </div>
            <button
              type="button"
              onClick={openAddForm}
              className="inline-flex items-center justify-center h-10 w-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
              aria-label="Add account"
            >
              <i className="fa-solid fa-plus" />
            </button>
          </div>

          {/* Desktop: search left (1 col width), add far right */}
          <div className="hidden sm:grid gap-4 w-full" style={gridStyle}>
            <div className="relative min-w-0">
              <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search"
                className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-gray-400"
                aria-label="Search passwords"
              />
            </div>
            <button
              type="button"
              onClick={openAddForm}
              style={{ gridColumn: gridColumns }}
              className="inline-flex items-center justify-center gap-2 h-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 text-sm font-medium justify-self-end"
              aria-label="Add account"
            >
              <i className="fa-solid fa-plus" />
              Add account
            </button>
          </div>
        </header>

        {loading && (
          <p className="text-center text-gray-600 py-8">Loading passwords...</p>
        )}

        {error && (
          <p className="text-center text-red-600 py-8">{error}</p>
        )}

        {!loading && !error && filteredPasswords.length === 0 && (
          <p className="text-center text-gray-600 py-8">No passwords found.</p>
        )}

        {!loading && !error && filteredPasswords.length > 0 && (
          <div className="grid gap-4 w-full" style={gridStyle}>
            {filteredPasswords.map((password) => (
              <PasswordCard
                key={password.id}
                password={password}
                onEdit={openEditForm}
                onDelete={openDeleteModal}
              />
            ))}
          </div>
        )}

      <AccountFormModal
        isOpen={formModal.open}
        mode={formModal.mode}
        initialValues={formModal.password}
        onClose={closeFormModal}
        onSave={handleSave}
        saving={saving}
        error={formError}
      />

      <ConfirmModal
        isOpen={deleteModal.open}
        title="Delete account"
        message={`Are you sure you want to delete "${deleteModal.password?.account}"? This action cannot be undone.`}
        error={deleteError}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteModal}
        variant="danger"
        loading={deleting}
      />
    </>
  );
};

export default PasswordsList;
